
import { defineComponent, ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import strategyApi from '@/api/strategy'
import moment from 'moment'
import { zh_tran, objTran } from '@/utils/lang.js'
import { jumpToStockChart, getStockClass } from '@/utils/stock'
import YieldChart from './subscription/YieldChart.vue'
import PositionPanel from './subscription/PositionPanel.vue'
import Nodata from '@/components/page/nodata.vue'
import LoadingMask from '@/components/LoadingMask.vue'

interface StrategyItem {
  code: string
  name: string
  tags?: string[]
  profile?: string
  increaseDay1?: number
  yearReturns?: number
  maxDrawDown?: number
  timePeriod?: number | string
  holdPeriod?: number
  is_favorite?: number
  hasGroupPermission?: boolean
  benchmarkReturns?: { benchmarkCode: string }[]
}

interface HistoryRow {
  symbol?: string
  name?: string
  market?: string
  time?: number
  tradeWay?: number
}

const HISTORY_PAGE_SIZE = 20

const BENCHMARK_MARKET_MAP: Record<string, 'HK' | 'US'> = {
  HSI: 'HK',
  SPX: 'US',
}

// timePeriod：1 近3月，2 近1年，3 今年以来，4 全部
const TIME_PERIOD_LABEL: Record<number, string> = {
  1: '近3月',
  2: '近1年',
  3: '今年以来',
  4: '全部',
}

export default defineComponent({
  name: 'CategoryView',
  components: { YieldChart, PositionPanel, Nodata, LoadingMask },
  setup() {
    const route = useRoute()
    const isDark = computed(() => route.query.theme !== 'light')
    const lang = (route.query['Accept-Language'] as string) || 'zh-Hans'
    const isTraditional = computed(() => lang === 'zh-Hant')

    function tran(text: string): string {
      if (!text) return text
      if (isTraditional.value) return (text as any).tran()
      return text
    }

    // ── 策略列表 ──────────────────────────────────────────────
    const strategyList = ref<StrategyItem[]>([])
    const activeSubTab = ref('')
    const loading = ref(false)

    const activeStrategy = computed<StrategyItem | null>(
      () => strategyList.value.find(s => s.code === activeSubTab.value) ?? null
    )

    const activeMarket = computed<'HK' | 'US'>(() => {
      const code = activeStrategy.value?.benchmarkReturns?.[0]?.benchmarkCode ?? ''
      return BENCHMARK_MARKET_MAP[code] ?? 'HK'
    })

    const fetchStrategyList = async (category: string) => {
      loading.value = true
      strategyList.value = []
      try {
        const token = (route.query.token as string) || ''
        const res: any = await strategyApi.getStrategyList({ token, category })
        const list = res?.data?.list ?? []
        strategyList.value = isTraditional.value ? objTran('t', list) : list
        activeSubTab.value = list[0]?.code ?? ''
      } catch (e) {
        console.error('获取策略列表失败', e)
      } finally {
        loading.value = false
      }
    }

    watch(
      () => route.params.category,
      (category) => {
        strategyList.value = []
        activeSubTab.value = ''
        if (category) fetchStrategyList(category as string)
      },
      { immediate: true }
    )

    // ── 格式化工具 ────────────────────────────────────────────
    const formatPct = (val?: number) => {
      if (val == null) return '--'
      const pct = val * 100
      return `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`
    }

    const formatAbsPct = (val?: number) => {
      if (val == null) return '--'
      return `${Math.abs(val * 100).toFixed(2)}%`
    }

    const pctClass = (val?: number) => {
      if (val == null) return ''
      return val > 0 ? 'up' : val < 0 ? 'down' : ''
    }

    const holdPeriodText = (val?: number) => {
      if (val == null) return '--'
      return val === 0 ? tran('不定期') : `${val}${tran('日')}`
    }

    const getTimePeriodLabel = (timePeriod?: number | string) => {
      const period = Number(timePeriod) || 2
      return TIME_PERIOD_LABEL[period] ?? TIME_PERIOD_LABEL[2]
    }

    const periodReturnsLabel = (timePeriod?: number | string) =>
      tran(`${getTimePeriodLabel(timePeriod)}收益`)

    const periodDrawDownLabel = (timePeriod?: number | string) =>
      tran(`${getTimePeriodLabel(timePeriod)}最大回撤`)

    const fmtDate = (ts?: number) => (ts ? moment(ts).format('YYYY-MM-DD') : '--')

    // ── 延时 / 市场标签 ───────────────────────────────────────
    const hkDelay = route.query.HKDELAY === '1'
    const usDelay = route.query.USDELAY === '1'
    const prefer = (route.query.preferGreenUptrendRedDowntrend as string) || '0'

    const isHKMarket = (market: string) => /hk/i.test(market) || market.toLowerCase() === 'hkse'

    const getMarketLabel = (market: string) => {
      const m = market.toLowerCase()
      return isHKMarket(m)
       ? 'HK'
       : 'US'

      // if (m === 'nyse' || m === 'nasdaq' || /us/.test(m)) return 'US'

        return market.toUpperCase()
    }

    const isDelayed = (market: string) => {
      if (!market) return false
      return (isHKMarket(market) && hkDelay) || (!isHKMarket(market) && usDelay)
    }

    // ── 历史调仓 ──────────────────────────────────────────────
    const historyList = ref<HistoryRow[]>([])
    const historyLoading = ref(false)
    const historyPageNo = ref(1)
    const historyHasMore = ref(true)

    const fetchHistory = async (code: string, pageNo = 1) => {
      if (historyLoading.value) return
      historyLoading.value = true
      try {
        const token = (route.query.token as string) || ''
        const res: any = await strategyApi.getStockHistory({ token, code, pageNo, pageSize: HISTORY_PAGE_SIZE })
        const list: HistoryRow[] = res?.data?.list ?? []
        historyList.value = pageNo === 1 ? list : historyList.value.concat(list)
        historyHasMore.value = list.length === HISTORY_PAGE_SIZE
        historyPageNo.value = pageNo
      } catch (e) {
        console.error('获取历史调仓失败', e)
      } finally {
        historyLoading.value = false
      }
    }

    const onHistoryScroll = (e: Event) => {
      const el = e.target as HTMLElement
      if (!historyHasMore.value || historyLoading.value) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        const code = activeStrategy.value?.code
        if (code) fetchHistory(code, historyPageNo.value + 1)
      }
    }

    // 切换策略时重置历史列表
    watch(activeSubTab, (code) => {
      historyList.value = []
      historyPageNo.value = 1
      historyHasMore.value = true
      if (code) fetchHistory(code, 1)
    }, { immediate: true })

    // ── 订阅 / 取消订阅 ───────────────────────────────────────
    const favoriteLoading = ref(false)

    const toggleFavorite = async () => {
      const strategy = activeStrategy.value
      if (!strategy?.code || favoriteLoading.value) return
      favoriteLoading.value = true
      try {
        const token = (route.query.token as string) || ''
        const newStatus = strategy.is_favorite === 1 ? 0 : 1
        await strategyApi.favoriteStrategy({ token, code: strategy.code, status: newStatus as 0 | 1 })
        const target = strategyList.value.find(s => s.code === strategy.code)
        if (target) target.is_favorite = newStatus
      } catch (e) {
        console.error('订阅操作失败', e)
      } finally {
        favoriteLoading.value = false
      }
    }

    // ── 历史调仓表格 CSS 变量（避免模板内联冗长对象）────────────
    const historyTableVars = computed(() => ({
      '--tp-bg': isDark.value ? '#111424' : '#FFFFFF',
      '--tp-border': isDark.value ? 'rgba(255,255,255,0.06)' : '#e8e8e8',
      '--tp-header-bg': isDark.value ? '#1A1D2F' : '#F0F1F7',
      '--tp-header-text': isDark.value ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
      '--tp-th-color': isDark.value ? '#7C7E87' : '#808080',
      '--tp-th-bg': isDark.value ? 'rgba(255,255,255,0.04)' : '#f5f5f5',
      '--tp-text': isDark.value ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
      '--tp-text-sub': isDark.value ? '#FFFFFF73' : '#7D7D7D',
      '--tp-text-muted': isDark.value ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
      '--tp-row-hover': isDark.value ? 'rgba(255,255,255,0.05)' : '#f0f2f5',
    }))

    const goStockDetail = (row: HistoryRow) => {
      const market = (row.market || '').toUpperCase()
      jumpToStockChart(`${row.symbol}.${market}`)
    }

    return {
      isDark, tran,
      activeSubTab, strategyList, loading,
      activeStrategy, activeMarket,
      formatPct, formatAbsPct, pctClass, holdPeriodText, periodReturnsLabel, periodDrawDownLabel, fmtDate,
      isHKMarket, getMarketLabel, isDelayed,
      historyList, historyLoading, historyHasMore, onHistoryScroll,
      favoriteLoading, toggleFavorite,
      historyTableVars, goStockDetail,
      getStockClass, prefer,
    }
  }
})
